import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "../../components/CustomToast";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import {
  buildOffHoursClearPayload,
  buildOffHoursPayload,
  extractApiErrorMessage,
  validateOffHoursWindow,
} from "../../lib/offHours";
import { useAdminSettings } from "./use-admin-settings";

const TIME_INPUT =
  "rounded-lg border border-line bg-elevated px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

const BUTTON_PRIMARY =
  "rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-dark disabled:opacity-50";
const BUTTON_SECONDARY =
  "rounded-lg bg-soft px-4 py-2 text-xs font-bold text-ink-2 transition-colors hover:bg-soft/70 disabled:opacity-50";

/**
 * The nightly window in which the autoscaler scales the voice infrastructure down.
 *
 * Moved here from Current Stats: it is a rarely-changed platform-wide control,
 * not a live operational readout, and it should not sit among the pause toggles
 * an operator reaches for during an incident.
 */
export function OffHoursSection() {
  const { data, isLoading, patch } = useAdminSettings();

  const saved = data?.autoscaler?.off_hours;
  const savedStart = saved?.start || "";
  const savedEnd = saved?.end || "";
  const isUnset = !savedStart && !savedEnd;

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<null | { title: string; body: string; onConfirm: () => void }>(
    null
  );

  // Re-seed the inputs only when the stored window itself changed. Without the
  // guard a refetch would wipe out a half-typed edit.
  const lastSynced = useRef<string | null>(null);
  useEffect(() => {
    const key = `${savedStart}|${savedEnd}`;
    if (lastSynced.current === key) return;
    lastSynced.current = key;
    setStart(savedStart);
    setEnd(savedEnd);
  }, [savedStart, savedEnd]);

  const validation = validateOffHoursWindow(start, end);
  const dirty = start !== savedStart || end !== savedEnd;

  const save = async (
    payload: Parameters<typeof patch>[0],
    successMessage: string,
    failureMessage: string
  ) => {
    setSaving(true);
    try {
      await patch(payload);
      toast.success(successMessage);
    } catch (error) {
      console.error(failureMessage, error);
      toast.error(extractApiErrorMessage(error, failureMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    setConfirm({
      title: "Confirm off-hours window",
      body: "This takes effect within about 3 minutes. If the current time falls inside this window, the autoscaler will start scaling infrastructure down.",
      onConfirm: async () => {
        setConfirm(null);
        await save(
          buildOffHoursPayload(start, end),
          `Off-hours window set to ${start} - ${end} IST.`,
          "Failed to update off-hours window"
        );
      },
    });
  };

  const handleReset = () => {
    setConfirm({
      title: "Reset off-hours window",
      body: "This clears the override and hands the window back to the deployed default. It takes effect within about 3 minutes.",
      onConfirm: async () => {
        setConfirm(null);
        await save(buildOffHoursClearPayload(), "Off-hours window reset to defaults", "Failed to reset off-hours window");
      },
    });
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-line bg-elevated p-6 shadow-sm">
        <p className="flex items-center gap-2 text-xs text-ink-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading off-hours window…
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line bg-elevated p-6 shadow-sm">
      <h2 className="text-sm font-bold text-ink">Off-Hours</h2>
      <p className="mt-1 text-xs text-ink-2">
        The nightly window in which the voice infrastructure is scaled down and brought back up. Times
        are IST (Asia/Kolkata).
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink">Start (IST)</span>
          <input
            type="time"
            aria-label="Off-hours start time (IST)"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className={TIME_INPUT}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink">End (IST)</span>
          <input
            type="time"
            aria-label="Off-hours end time (IST)"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className={TIME_INPUT}
          />
        </label>
        <div className="flex gap-2 sm:pb-0.5">
          <button
            onClick={handleSave}
            disabled={!validation.ok || !dirty || saving}
            className={`${BUTTON_PRIMARY} flex items-center gap-2`}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save window
          </button>
          <button onClick={handleReset} disabled={isUnset || saving} className={BUTTON_SECONDARY}>
            Reset to default
          </button>
        </div>
      </div>

      <div className="mt-3 space-y-1">
        {isUnset ? (
          <p className="text-[11px] text-ink-2">Not set, using the deployed default.</p>
        ) : (
          <p className="text-[11px] text-ink-2">
            Current window: {savedStart} - {savedEnd} IST.
            {savedStart > savedEnd ? " Runs overnight into the next morning." : ""}
          </p>
        )}
        {!validation.ok && dirty && (
          <p className="text-[11px] font-medium text-amber-600">{validation.error}</p>
        )}
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          body={confirm.body}
          confirmLabel="Confirm"
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}